import React, { useState, useEffect } from 'react';
import {
  CheckCircle2,
  Activity,
} from 'lucide-react';
import axios from '../../services/axios';
import { Pagination } from "antd";
 
const Dashboard = () => {
  const [categorized, setCategorized] = useState({
    today: [],
    week: [],
    month: [],
    overdue: [],
    critical: [],
    completed: [] 
  });
  const [loading, setLoading] = useState(false);
  const [floatAnalysis, setFloatAnalysis] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [allSchedules, setAllSchedules] = useState([]);
  const [overdueFilter, setOverdueFilter] = useState("notSubmitted");
  const projectName = JSON.parse(localStorage.getItem('Project'));
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('today');
  const itemsPerPage = 3;

  const [stats, setStats] = useState({
    totalTasks: 0,
    onSchedule: 0,
    delayed: 0,
    criticalTasks: 0,
    avgFloat: 0,
    completionRate: 0
  });

  // Get tasks for active tab
  let activeTasks = categorized[activeTab] || [];

  // Apply overdue sub-filters
  if (activeTab === "overdue") {
    activeTasks =
      overdueFilter === "notSubmitted"
        ? activeTasks.filter(t => t.isNotSubmittedOverdue)
        : activeTasks.filter(t => t.isNotApprovedOverdue);
  }

  // Paginated tasks
  const paginatedTasks = activeTasks.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    fetchDashboardData();
  }, [projectName]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const res = await axios.post("/fetch-dashboard-schedule", { projectName });

      if (res.data?.success) {
        const cat = res.data.categorized || {};

        setCategorized({
          today: cat.today || [],
          week: cat.week || [],
          month: cat.month || [],
          overdue: cat.overdue || [],
          critical: cat.critical || [],
          completed: cat.completed || [] 
        });

        const allSchedules = [
          ...(cat.today || []),
          ...(cat.week || []),
          ...(cat.month || []),
          ...(cat.overdue || []),
          ...(cat.critical || []),
          ...(cat.completed || []) 
        ];

        setAllSchedules(allSchedules);

        setFloatAnalysis(
          allSchedules.map(s => ({
            name: s.reference || s.projectName,
            totalFloat: s.totalfloat || 0,
            freeFloat: s.freefloat || 0,
            progress: s.plannedNumberOfDocuments
              ? (s.uploadedCount / s.plannedNumberOfDocuments) * 100
              : 0
          }))
        );

        setTimelineData(
          allSchedules.map(s => ({
            name: s.reference || s.projectName,
            start: new Date(s.earlystart || s.actualStart).getTime(),
            end: new Date(s.earlyfinish || s.actualFinish).getTime(),
            late: new Date(s.latefinish).getTime(),
            progress: s.plannedNumberOfDocuments
              ? (s.uploadedCount / s.plannedNumberOfDocuments) * 100
              : 0,
            isCritical: Number(s.totalfloat) === 0
          }))
        );

        const totalDocs = allSchedules.reduce(
          (sum, s) => sum + (s.plannedNumberOfDocuments || 0),
          0
        );

        const uploadedDocs = allSchedules.reduce(
          (sum, s) => sum + (s.uploadedCount || 0),
          0
        );

        const now = new Date();

        const onSchedule = allSchedules.filter(s => {
          const progress = s.plannedNumberOfDocuments
            ? (s.uploadedCount / s.plannedNumberOfDocuments) * 100
            : 0;

          const timeElapsed =
            ((now - new Date(s.plannedstartDate)) /
              (new Date(s.plannedendDate) - new Date(s.plannedstartDate))) * 100;

          return progress >= timeElapsed;
        }).length;

        setStats({
          totalTasks: allSchedules.length,
          onSchedule,
          delayed: allSchedules.length - onSchedule,
          criticalTasks: (cat.critical || []).length,
          avgFloat: allSchedules.length
            ? allSchedules.reduce((sum, s) => sum + (s.totalfloat || 0), 0) /
              allSchedules.length
            : 0,
          completionRate: totalDocs ? (uploadedDocs / totalDocs) * 100 : 0
        });
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getWeekRange = () => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday
    const sunday = new Date(now);
    sunday.setDate(now.getDate() - dayOfWeek);
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);

    const format = (d) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${format(sunday)} - ${format(saturday)}, ${now.getFullYear()}`;
  };

  const getMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const format = (d) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${format(firstDay)} - ${format(lastDay)}, ${now.getFullYear()}`;
  };

  const getScheduleHealth = (schedule) => {
    const progress = (schedule.uploadedCount / schedule.plannedNumberOfDocuments) * 100 || 0;
    const now = new Date();
    const timeElapsed =
      ((now - new Date(schedule.plannedstartDate)) /
        (new Date(schedule.plannedendDate) - new Date(schedule.plannedstartDate))) *
      100;

    if (schedule.totalfloat === 0) {
      return { status: 'ok', color: 'green', label: 'OK' };
    }
    if (progress < timeElapsed - 10) {
      return { status: 'delayed', color: 'red', label: 'Delayed' };
    }
    if (progress < timeElapsed) {
      return { status: 'at-risk', color: 'yellow', label: 'At Risk' };
    }
    return { status: 'on-track', color: 'green', label: 'On Track' };
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading schedule analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Active Schedules Section */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mb-8">
          <div className="flex flex-col space-y-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-3 rounded-xl">
                  <Activity className="text-white" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {activeTab === "today" && "Active Schedules Today"}
                    {activeTab === "week" && "This Week’s Schedules"}
                    {activeTab === "month" && "This Month’s Schedules"}
                    {activeTab === "critical" && "Critical Path Tasks"}
                    {activeTab === "overdue" && "Overdue Schedules"}
                    {activeTab === "completed" && "Completed Schedules"}
                  </h2>

                  {/* Date Range Badge */}
                  {(activeTab === "today" ||
                    activeTab === "week" ||
                    activeTab === "month") && (
                    <div className="mt-2">
                      <span className="inline-block bg-blue-100 text-blue-800 text-sm font-semibold px-4 py-1 rounded-full shadow-sm">
                        {activeTab === "today" && getTodayDate()}
                        {activeTab === "week" && getWeekRange()}
                        {activeTab === "month" && getMonthRange()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <span className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-bold">
                {activeTasks.length} Active
              </span>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-3 mt-2">
              {[
                { key: "today", label: "Today" },
                { key: "week", label: "This Week" },
                { key: "month", label: "This Month" },
                { key: "critical", label: "Critical" },
                { key: "overdue", label: "Overdue" },
                { key: "completed", label: "Completed" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setCurrentPage(1);
                  }}
                  className={`px-5 py-2 rounded-full font-semibold text-sm transition-all duration-200 ${
                    activeTab === tab.key
                      ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg"
                      : "bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ✅ Overdue filters BELOW TABS */}
            {activeTab === "overdue" && (
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => {
                    setOverdueFilter("notSubmitted");
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-semibold ${
                    overdueFilter === "notSubmitted"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-800"
                  }`}
                >
                  Not Yet Submitted
                </button>

                <button
                  onClick={() => {
                    setOverdueFilter("notApproved");
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-semibold ${
                    overdueFilter === "notApproved"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-800"
                  }`}
                >
                  Not Yet Approved
                </button>
              </div>
            )}
          </div>

          {activeTasks.length === 0 ? (
            <div className="text-center py-16">
              <div className="bg-gray-100 p-6 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                <CheckCircle2 className="text-gray-400" size={48} />
              </div>
              <p className="text-gray-500 text-lg">No schedules in this category</p>
            </div>
          ) : (
            <div className="space-y-6">
              {paginatedTasks.map(task => {
                const health = getScheduleHealth(task);
                const progress = task.plannedNumberOfDocuments
                  ? (task.uploadedCount / task.plannedNumberOfDocuments) * 100
                  : 0;

                return (
                  <div key={task._id} className="bg-gradient-to-r from-gray-50 to-blue-50 border-2 border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex-1">
                        <div className="grid grid-cols-4 gap-6 mb-4 items-start">
                          <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Type</p>
                            <div className="flex flex-nowrap gap-2 overflow-x-auto">
                              {task.type.map((type, idx) => (
                                <span key={idx} className="bg-white border-2 border-blue-200 text-blue-700 px-3 py-1 rounded-lg text-xs font-semibold whitespace-nowrap">
                                  {type}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Categories</p>
                            <div className="flex flex-nowrap gap-2 overflow-x-auto">
                              {task.category.map((cat, idx) => (
                                <span key={idx} className="bg-white border-2 border-blue-200 text-blue-700 px-3 py-1 rounded-lg text-xs font-semibold whitespace-nowrap">
                                  {cat}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Discipline</p>
                            <div className="flex flex-nowrap gap-2 overflow-x-auto">
                              {(Array.isArray(task.discipline) ? task.discipline : [task.discipline]).map((disc, idx) => (
                                <span key={idx} className="bg-white border-2 border-purple-200 text-purple-700 px-3 py-1 rounded-lg text-xs font-semibold whitespace-nowrap">
                                  {disc}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">No. of Document</p>
                            <div className="flex flex-nowrap gap-2">
                              <span className={`bg-white border-2 ${task.plannedNumberOfDocuments > 0 ? "border-green-200 text-green-700" : "border-red-200 text-red-700"} px-3 py-1 rounded-lg text-xs font-semibold whitespace-nowrap`}>
                                {task.plannedNumberOfDocuments}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 p-4 bg-white rounded-xl border-2 border-gray-200">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Planned Start</p>
                            <p className="font-bold text-sm">{formatDate(task.plannedstartDate)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Planned Finish</p>
                            <p className="font-bold text-sm">{formatDate(task.plannedendDate)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Planned Duration</p>
                            <p className="font-bold text-sm">{task.plannedduration}</p>
                          </div>
                          {/* <div>
                            <p className="text-xs text-gray-500 mb-1">Early Start</p>
                            <p className="font-bold text-sm">{formatDate(task.earlystart)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Early Finish</p>
                            <p className="font-bold text-sm">{formatDate(task.earlyfinish)}</p>
                          </div> */}
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Actual Start</p>
                            <p className="font-bold text-sm text-blue-700">
                              {task.actualStart ? formatDate(task.actualStart) : "—"}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Actual Finish</p>
                            <p className="font-bold text-sm text-blue-700">
                              {task.actualFinish ? formatDate(task.actualFinish) : "—"}
                            </p>
                          </div>
                          {/* <div>
                            <p className="text-xs text-gray-500 mb-1">Late Start</p>
                            <p className="font-bold text-sm text-orange-600">{formatDate(task.latestart)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Late Finish</p>
                            <p className="font-bold text-sm text-orange-600">{formatDate(task.latefinish)}</p>
                          </div> */}
                        </div>

                        {/* gap between sections */}
                        <div className="mt-4 flex-1 mr-6">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-bold text-gray-700">
                              Progress: {task.uploadedCount}/{task.plannedNumberOfDocuments}
                            </span>
                            <span className="text-lg font-bold text-blue-600">
                              {progress.toFixed(1)}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                            <div
                              className={`h-4 rounded-full transition-all duration-500 ${
                                health.color === 'red'
                                  ? 'bg-gradient-to-r from-red-500 to-red-600'
                                  : health.color === 'yellow'
                                  ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                                  : 'bg-gradient-to-r from-green-500 to-green-600'
                              }`}
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      <div className="text-right ml-6 space-y-4">
                        {/* <div className="bg-gradient-to-br from-blue-500 to-purple-500 text-white rounded-xl p-4 min-w-[180px]">
                          <p className="text-xs opacity-90 mb-1">Duration</p>
                          <p className="text-3xl font-bold">{task.plannedduration}</p>
                          <p className="text-xs opacity-90">days planned</p>
                        </div> */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-4 min-w-[180px]">
                          <p className="text-xs text-gray-600 mb-1">Remaining</p>
                          <p className="text-2xl font-bold text-orange-600">{task.remainingDuration}</p>
                          <p className="text-xs text-gray-600">days left</p>
                        </div>
                        {task.actualDays !== null && (
                          <div className="bg-red-100 border-2 border-red-300 rounded-xl p-4 min-w-[180px]">
                            <p className="text-xs text-gray-600 mb-1">Actual Days</p>
                            <p className="text-2xl font-bold text-red-600">{task.actualDays}</p>
                            <p className="text-xs text-gray-600">days passed</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Pagination */}
              {activeTasks.length > itemsPerPage && (
                <div className="flex justify-center mt-6">
                  <Pagination
                    current={currentPage}
                    pageSize={itemsPerPage}
                    total={activeTasks.length}
                    onChange={(page) => setCurrentPage(page)}
                    showSizeChanger={false}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;